import { useState, useMemo } from 'react';
import { useRange } from 'react-instantsearch';

import InstantSearchAccordion from './units/instantSearchAccordion';
import InstantSearchFilterOption from './units/instantSearchFilterOption';
import TabToggle from './tabToggle';

import useCurrentSortOrder from '../hooks/useCurrentSortOrder';

import type { FilterBaseProps } from '@javascript/types/InstantSearch';

export interface EngineSizeFilterProps extends FilterBaseProps {
  engineSizes: {
    maximum: { label: string; value: number | undefined }[];
    minimum: { label: string; value: number | undefined }[];
  };
}

type MinOrMax = 'minimum' | 'maximum';

const EngineSizeFilter = ({
  engineSizes,
  filtersOpen,
}: EngineSizeFilterProps) => {
  const [minOrMax, setMinOrMax] = useState<MinOrMax>('minimum');

  const index = useCurrentSortOrder();

  const {
    refine,
    sendEvent,
    start: [startingFrom, startingTo],
  } = useRange({
    attribute: 'engineSize',
    // Max set to 7000 because it is above the maximum engine size of items in Algolia index, so skirts around the not filtering above that limit issue
    max: 7000,
    // Min set to 500 because it is below the minimum engine size of items in Algolia index, so skirts around the not filtering below that limit issue
    min: 0,
  });

  // If the range is set to 7000, it means no maximum has been set, so we treat it as Infinity
  const to = startingTo === 7000 ? Infinity : startingTo;
  const from = startingFrom === 0 ? -Infinity : startingFrom;

  const minValue = from === -Infinity ? undefined : from;
  const maxValue = to === Infinity ? undefined : to;

  const handleChange = (value?: number) => {
    const searchValues: [number | undefined, number | undefined] =
      minOrMax === 'minimum' ? [value, maxValue] : [minValue, value];

    //TODO: follow up with Algolia on why we need to do this to see events firing
    sendEvent({
      insightsMethod: 'clickedFilters',
      widgetType: 'ais.numeric-menu',
      eventType: 'click',
      payload: {
        eventName: 'Filter Applied',
        filters: [`engineSize:${value}`],
        index,
      },
      attribute: 'engineSize',
    });

    refine(searchValues);
  };

  const displaySelectedOptions = () => {
    const minLabel = engineSizes.minimum.find(
      (item) => item.value === from
    )?.label;
    const maxLabel = engineSizes.maximum.find(
      (item) => item.value === to
    )?.label;

    if (!minLabel && !maxLabel) return;
    if (!minLabel) return `Up to ${maxLabel}`;

    if (!maxLabel) return `From ${minLabel}`;

    if (minLabel === maxLabel) return minLabel;

    return `${minLabel} to ${maxLabel}`;
  };

  const availableOptions = useMemo(() => {
    const options =
      minOrMax === 'minimum' ? engineSizes.minimum : engineSizes.maximum;
    const limit = minOrMax === 'minimum' ? maxValue : minValue;

    if (limit === undefined) return options;

    return options.filter((item) =>
      minOrMax === 'minimum'
        ? !item.value || item.value <= limit
        : !item.value || item.value >= limit
    );
  }, [engineSizes, minOrMax, minValue, maxValue]);

  return (
    <InstantSearchAccordion
      filtersOpen={filtersOpen}
      label="Engine size"
      selectedOptions={displaySelectedOptions()}
    >
      <div className="ch-mb--3">
        <TabToggle
          onChange={setMinOrMax}
          option1={{ label: 'Minimum', value: 'minimum' }}
          option2={{ label: 'Maximum', value: 'maximum' }}
          selectedOption={minOrMax}
        />
      </div>
      <ul aria-label="Please select a range option">
        {availableOptions.map(({ value, label }) => (
          <InstantSearchFilterOption.Radio
            attribute="engineSize"
            isSelected={
              value === (minOrMax === 'minimum' ? minValue : maxValue)
            }
            key={`${minOrMax}-${label}`}
            label={label}
            onChange={() => handleChange(value)}
          />
        ))}
      </ul>
    </InstantSearchAccordion>
  );
};

export default EngineSizeFilter;
